/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { get, isEqual } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';

import {
  AvailableColumnCalculations,
  AvailableLineData,
  FinancialDatasetRequest,
  ForecastComparisonRequest,
  ForecastComparisonResponse,
  ImageResponse,
  LiveReportDetail,
  PerformanceMetricsBalanceSheet,
  PerformanceMetricsCashflow,
  PerformanceMetricsProfitAndLoss,
  ReportTemplateDetail,
  ReportTemplateRequest,
  ReportingTemplateFormula,
  RowFormula,
  SingleOrgForecastComparisonRequest
} from '@futrli-sdk/predict';
import { DispatchedReportItem, ReportScheduleItem } from '@futrli-sdk/report_scheduling';
import { ERROR_STATUS, getErrorStatus } from '@futrli/errors';
import { Logger, useStateRef } from '@futrli/shared-services';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';
import { useFinancialConfig } from '@futrli/views/hooks/useFinancialConfig';

import {
  fetchAvailableDatasources,
  fetchAvailableLineData,
  fetchAvailableRowFormulaData,
  fetchColumnCalculationsData,
  fetchReportImages,
  getReportBulkData,
  LiveReportPayload
} from '../core/api/reporting/report-builder';
import { getDispatchedReports, getScheduledReports } from '../core/api/reporting/report-scheduling';
import {
  fetchAvailableTemplateFormulaData,
  fetchTemplatesAvailableLineData
} from '../core/api/reporting/report-templates';
import { PredictManagementContext } from '../predict-management/context/predict-management-context';
import {
  fetchPerformanceMetricsBalanceSheet,
  fetchPerformanceMetricsCashflow,
  fetchPerformanceMetricsProfitAndLoss
} from '../core/api/performance-metrics';
import { fetchMultipleForecasts } from '../core/api/forecast';
import { LayoutItem as Layout } from './components/grid-layout';
import {
  AxisOptions,
  CalendarOptions,
  CARD_TYPES,
  DataField,
  FinData,
  FinFieldOptions,
  LayoutData,
  Margins,
  PageSetup,
  PAGE_NUMBERS_DISPLAY,
  PAGE_SIZES,
  ROUNDING_OPTIONS,
  TableStyle,
  TextData,
  WidgetData,
  WIDGET_TYPES,
  RowTypes,
  AppWidgetData,
  ImageData,
  ReportMappingData,
  ClassifierType,
  DatasetOptionType,
  MappingData,
  PERIOD_OPTIONS,
  COMPARISON_OPTIONS
} from './types';
import {
  actualForecastIds,
  UNAVAILABLE_DATASET_KIND,
  defaultColors,
  pageSizesOptions,
  defaultCalendarOptions,
  DEFAULT_PERIOD_NUMBER,
  SAVE_DELAY_TIME,
  GET_FIN_DATA_DELAY_TIME
} from './constants';
import {
  arePageLayoutsEqual,
  getDefaultGrouping,
  getFieldsByGroupingWithLevel,
  getStateFromReport,
  areLayoutsDataEqual,
  getFinFieldsWithoutChild,
  getFieldWithChildren,
  flattenFinFieldsToRefs,
  multiForecastQueryFromQueriesArr,
  withActualsForecastId,
  convertFinDataToIncludeForecastId,
  addDummyAccounts,
  flattenFinFields,
  updateFinFields,
  getDeletedFinFieldMappingEntries,
  hasDataset,
  hasField,
  localizeFieldNames,
  getReferencedDatasources,
  getUnavailableDatasetIds,
  getMissingDatasetName
} from './utils-data';
import { PERIODS } from '../forecast/detail/performance/components/period-dropdown';
import { getPageHeightFromSetup, getNumberOfPagesInTemplate } from './utils-layout';
import { usePredictionsInfoContext } from '../forecast/detail/context/predictions-info-context';
import { useConsolidatedForecastingContext } from '../forecast/detail/context/consolidated-forecasting-context';
import {
  AvailableDatasource,
  filterReportBuilderForecasts,
  getForecastsFromDatasources,
  ReportBuilderForecast
} from './available-forecasts';
import { useI18nConstants } from './use-i18n-constants';
import { useWidgetDataReload } from './use-widget-data-reload';
import {
  transformComparisonQueriesForV2,
  transformFinancialDataQueriesForV2,
  V2FinancialDatasetRequest
} from './utils-queries';
import { TableFiltersWithSort } from '../utils/table-filters/filter-table-types';

interface Props {
  report: LiveReportDetail | ReportTemplateDetail;
  patchReport: (reportId: string, data: LiveReportPayload & Partial<ReportTemplateRequest>) => Promise<void>;
  organisationId: string;
  workspaceId: string;
  isTemplate?: boolean;
  isMasterPage?: boolean;
  isViewPage?: boolean;
  isDashboardPage?: boolean;
}
export default function useReports({
  report,
  patchReport,
  organisationId,
  workspaceId,
  isTemplate,
  isMasterPage,
  isViewPage,
  isDashboardPage
}: Props) {
  const intl = useIntl();
  const { resourceSharing } = useFlags();
  const isResourceSharingEnabled = !!resourceSharing;
  const { nodeStaticIds } = useLocalisedConstantsContext();
  const {
    actualForecasts,
    datasetComparisonChildren,
    liveForecastName,
    getFinDataQueryForWidget,
    dummyTemplateForecasts,
    getComparisonQueryForWidget,
    getDataReferencesFromReport,
    getDummyTemplateDSInReport
  } = useI18nConstants();
  const {
    pageSetup: reportPageSetup,
    pageLayout: reportPageLayout,
    pageLayoutData: reportPageLayoutData,
    sourceTemplateId: reportSourceTemplateId,
    userTimezone,
    reportMappingData
  } = useMemo(() => getStateFromReport(report), [report]);
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { isConsolidation } = usePredictionsInfoContext();
  const {
    forecasts: consolidationForecasts,
    isLoading: consolidationForecastsLoading
  } = useConsolidatedForecastingContext();
  const {
    financialConfig,
    error: financialConfigError,
    isLoading: isFinancialConfigLoading
  } = useFinancialConfig({
    workspaceId,
    entityType: isConsolidation ? 'consolidation' : 'organisation',
    entityId: organisationId
  });

  // data state
  const [cashflowMetrics, setCashflowMetrics] = useState<PerformanceMetricsCashflow>();
  const [profitAndLossMetrics, setProfitAndLossMetrics] = useState<PerformanceMetricsProfitAndLoss>();
  const [balanceSheetMetrics, setBalanceSheetMetrics] = useState<PerformanceMetricsBalanceSheet>();
  const [isFetchingCFMetrics, setIsFetchingCFMetrics] = useState<boolean>(false);
  const [isFetchingPandLMetrics, setIsFetchingPandLMetrics] = useState<boolean>(false);
  const [isFetchingBSMetrics, setIsFetchingBSMetrics] = useState<boolean>(false);
  const [availableFinFields, setAvailableFinFields] = useState<AvailableLineData>({
    profit_and_loss: [],
    balance_sheet: [],
    cash_flow: [],
    non_financial_drivers_and_groups: []
  });
  const [columnCalculations, setColumnCalculations] = useState<AvailableColumnCalculations>();
  const [rowFormulaData, setRowFormulaData] = useState<(RowFormula | ReportingTemplateFormula)[]>();
  const [futrliFormulaData, setFutrliFormulaData] = useState<ReportingTemplateFormula[]>();
  const [pageSetup, setPageSetup] = useState<PageSetup>(reportPageSetup);
  const [pageLayout, setPageLayout] = useState<Layout[]>(reportPageLayout);
  const [pageLayoutData, setPageLayoutData] = useState<LayoutData[]>(reportPageLayoutData);
  //view state
  const [currentCardId, setCurrentCardId] = useState('');
  const [currentCard, setCurrentCard] = useState<LayoutData | undefined>();
  const [layoutFinData, setLayoutFinData, layoutFinDataRef] = useStateRef<
    Record<string, Record<string, FinData> | undefined>
  >({});
  const [layoutComparisonData, setLayoutComparisonData] = useState<
    Record<string, ForecastComparisonResponse | undefined>
  >({});

  const [finDataRequestsFinished, setFinDataRequestsFinished] = useState(false);
  const [comparisonDataRequestsFinished, setComparisonDataRequestsFinished] = useState(false);
  const [allRequestsFinished, setAllRequestsFinished] = useState(false);

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(false);
  const [initialDataFetched, setInitialDataFetched] = useState(false);
  const [isFetchingFinData, setIsFetchingFinData] = useState(false);

  const [isFetchingComparisonData, setIsFetchingComparisonData] = useState(false);
  const [isFetchingScheduleData, setIsFetchingScheduleData] = useState(false);
  const [scheduledReports, setScheduleReports] = useState<ReportScheduleItem[] | undefined>(undefined);
  const [dispatchedReports, setDispatchedReports] = useState<DispatchedReportItem[] | undefined>(undefined);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isPendingNewFinData, setIsPendingNewFinData] = useState(false);
  const [workAreaWidth, setWorkAreaWidth] = useState(0);
  const [allFinFields, setAllFinFields] = useState<DataField[]>([]);
  const [allFinFieldsArray, setAllFinFieldsArray] = useState<DataField[]>([]);
  const [allForecasts, setAllForecasts] = useState<ReportBuilderForecast[]>([]);
  const [forecastsFetched, setForecastsFetched] = useState(false);
  const [availableDatasources, setAvailableDatasources] = useState<AvailableDatasource[]>();
  const [mapDatasetsModalOpen, setMapDatasetsModalOpen] = useState<boolean>(false);
  const [mapTemplateDataModalOpen, setMapTemplateDataModalOpen] = useState<boolean>(false);
  const [datasetsToMap, setDatasetsToMap] = useState<string[]>([]);
  const [dataRefsToMap, setDataRefsToMap] = useState<string[]>([]);
  const [itemToBringIntoView, setItemToBringIntoView] = useState<string | null>(null);
  const [scrollReportTo, setScrollReportTo] = useState<[number, number]>();

  const savingTimerId = useRef<number | null>(null);
  const stateInitialized = useRef(false);
  const gettingComparisonDataTimerId = useRef<number | null>(null);
  const headerFooterLimits = useRef<number[][]>([]);
  const heightAdjusted = useRef<boolean>(false);
  const [pageBreaks, setPageBreaks] = useState(1);
  const reportData = useRef({
    pageSetup: reportPageSetup,
    pageLayout: reportPageLayout,
    pageLayoutData: reportPageLayoutData
  });
  const [uploadedImages, setUploadedImages] = useState<ImageResponse[]>([]);
  const [cashFlowAndBalanceSheetRefs, setCashFlowAndBalanceSheetRefs] = useState<string[]>([]);
  const [dataReferenceMapping, setDataReferenceMapping] = useState<ReportMappingData | undefined>(reportMappingData);
  const [locale, setLocale] = useState(report.locale);

  const isViewReportPage = isViewPage;

  const isWorkspaceTemplate = (report as ReportTemplateDetail).kind === 'workspace';
  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;
  // Futrli templates are not live reports (nor shareable workspace templates), so the financial-data
  // endpoint has nothing to look up by id. It only serves placeholder data for them, as before.
  const financialDataReportId = isFutrliTemplate ? undefined : report.id;
  // For reports, the datasets come from the available datasources endpoint, which only returns the datasources
  // shared with the user for this report (including the ones the user has no access to).
  // Templates still read the organisation forecasts list.
  // Futrli templates don't read any real datasets: they offer the hardcoded dummy "Forecast 1" (budget) and
  // "Forecast 2" (scenario) alongside the actuals, which are mapped to real datasets when a report is created.
  const forecasts = useMemo(() => {
    if (isFutrliTemplate) {
      return dummyTemplateForecasts.filter(forecast => forecast.kind !== 'live');
    }
    return isTemplate
      ? filterReportBuilderForecasts(allForecasts, isResourceSharingEnabled)
      : getForecastsFromDatasources(availableDatasources || []);
  }, [
    allForecasts,
    availableDatasources,
    dummyTemplateForecasts,
    isFutrliTemplate,
    isResourceSharingEnabled,
    isTemplate
  ]);
  const validForecastIds = useMemo(() => new Set(forecasts.map(forecast => forecast.id)), [forecasts]);

  const [reloadWidgetData, widgetsAwaitingData] = useWidgetDataReload(
    workspaceId,
    organisationId,
    !!isConsolidation,
    setLayoutFinData,
    layoutFinDataRef,
    setIsFetchingFinData,
    financialDataReportId,
    isWorkspaceTemplate
  );

  // Fetch the datasets available to the report (or the forecasts list for templates)
  useEffect(() => {
    const getForecasts = async () => {
      if (forecastsFetched) {
        return;
      }
      // Futrli templates use the hardcoded dummy forecasts, there is nothing to fetch
      if (isFutrliTemplate) {
        setForecastsFetched(true);
        return;
      }
      try {
        if (!isTemplate) {
          setAvailableDatasources(
            await fetchAvailableDatasources(workspaceId, organisationId, report.id, !!isConsolidation)
          );
          setForecastsFetched(true);
          return;
        }

        if (!isConsolidation) {
          setAllForecasts(await fetchMultipleForecasts(workspaceId, organisationId));
          setForecastsFetched(true);
          return;
        }

        if (!consolidationForecastsLoading && typeof consolidationForecasts !== 'undefined') {
          setAllForecasts(consolidationForecasts || []);
          setForecastsFetched(true);
        }
      } catch (error) {
        Logger.error('Could not get forecasts: ');
        Logger.error(error as string);
      }
    };
    getForecasts();
  }, [
    workspaceId,
    organisationId,
    report.id,
    isConsolidation,
    isTemplate,
    isFutrliTemplate,
    consolidationForecasts,
    consolidationForecastsLoading,
    forecastsFetched
  ]);

  useEffect(() => {
    if (financialConfigError && !financialConfig) {
      if (getErrorStatus(financialConfigError) !== ERROR_STATUS._404) {
        Logger.error(String(financialConfigError));
        addErrorMessage?.(new Error('Failed to fetch needed reporting data'));
      }
    }
  }, [addErrorMessage, financialConfigError, financialConfig]);

  // Wait for config, then use the existing flags to initialise metadata only once.
  useEffect(() => {
    if (!financialConfig || isFetchingInitialData || initialDataFetched) {
      return;
    }
    const fetchData = async () => {
      try {
        setIsFetchingInitialData(true);
        const [
          finFields,
          fetchedRowFormulaData,
          fetchedColumnCalculationsData,
          fetchedTemplateFormulasData,
          fetchedUploadedImages
        ] = await Promise.all([
          isFutrliTemplate
            ? await fetchTemplatesAvailableLineData(workspaceId, organisationId, isConsolidation)
            : await fetchAvailableLineData(workspaceId, organisationId, isConsolidation),
          isFutrliTemplate
            ? Promise.resolve(undefined)
            : fetchAvailableRowFormulaData(workspaceId, organisationId, isConsolidation),
          fetchColumnCalculationsData(workspaceId, organisationId, isConsolidation),
          fetchAvailableTemplateFormulaData(workspaceId, organisationId, isConsolidation),
          fetchReportImages(workspaceId, organisationId, isConsolidation)
        ]);
        if (finFields) {
          const adjustedFields = {
            profit_and_loss:
              isFutrliTemplate && !isConsolidation
                ? addDummyAccounts(finFields.profit_and_loss, 'transactional', organisationId)
                : finFields.profit_and_loss,
            balance_sheet:
              isFutrliTemplate && !isConsolidation
                ? addDummyAccounts(finFields.balance_sheet, 'cumulative', organisationId)
                : finFields.balance_sheet,
            cash_flow:
              isFutrliTemplate && !isConsolidation
                ? addDummyAccounts(finFields.cash_flow, 'cash_transactional', organisationId)
                : finFields.cash_flow,
            non_financial_drivers_and_groups: finFields.non_financial_drivers_and_groups || []
          };
          const localizedAdjustedFields = {
            profit_and_loss: localizeFieldNames(adjustedFields.profit_and_loss as DataField[], nodeStaticIds),
            balance_sheet: localizeFieldNames(adjustedFields.balance_sheet as DataField[], nodeStaticIds),
            cash_flow: localizeFieldNames(adjustedFields.cash_flow as DataField[], nodeStaticIds),
            non_financial_drivers_and_groups: localizeFieldNames(
              adjustedFields.non_financial_drivers_and_groups as DataField[],
              nodeStaticIds
            )
          };
          setAvailableFinFields(localizedAdjustedFields as AvailableLineData);
          const allFields: DataField[] = [
            {
              ref: 'profit_and_loss',
              name: intl.formatMessage({
                id: 'financial_fields.type.profit_and_loss',
                defaultMessage: 'Profit & Loss'
              }),
              children: localizedAdjustedFields.profit_and_loss as DataField[],
              classifier: ''
            },
            {
              ref: 'balance_sheet',
              name: intl.formatMessage({ id: 'financial_fields.type.balance_sheet', defaultMessage: 'Balance Sheet' }),
              children: localizedAdjustedFields.balance_sheet as DataField[],
              classifier: ''
            },
            {
              ref: 'cash_flow',
              name: intl.formatMessage({ id: 'financial_fields.type.cash_flow', defaultMessage: 'Cash Flow' }),
              children: localizedAdjustedFields.cash_flow as DataField[],
              classifier: ''
            },
            {
              ref: 'drivers',
              name: intl.formatMessage({ id: 'reporting.grouping.drivers', defaultMessage: 'Drivers' }),
              children: localizedAdjustedFields.non_financial_drivers_and_groups as DataField[],
              classifier: ''
            }
          ];
          setAllFinFields(allFields);
          setAllFinFieldsArray(flattenFinFields(allFields));
          setCashFlowAndBalanceSheetRefs(
            flattenFinFieldsToRefs([
              {
                ref: 'balance_sheet',
                name: intl.formatMessage({
                  id: 'financial_fields.type.balance_sheet',
                  defaultMessage: 'Balance Sheet'
                }),
                children: localizedAdjustedFields.balance_sheet as DataField[],
                classifier: ''
              },
              {
                ref: 'cash_flow',
                name: intl.formatMessage({ id: 'financial_fields.type.cash_flow', defaultMessage: 'Cash Flow' }),
                children: localizedAdjustedFields.cash_flow as DataField[],
                classifier: ''
              }
            ])
          );
        }
        setRowFormulaData([
          ...(fetchedRowFormulaData || []),
          ...(isFutrliTemplate ? fetchedTemplateFormulasData || [] : [])
        ]);
        setFutrliFormulaData(fetchedTemplateFormulasData);
        setColumnCalculations(fetchedColumnCalculationsData);
        setUploadedImages((fetchedUploadedImages || []).filter(img => img.status === 'ready'));
      } catch (error) {
        if (getErrorStatus(error) !== ERROR_STATUS._404) {
          // eslint-disable-next-line no-console
          console.error(JSON.stringify(error));
          addErrorMessage?.(new Error('Failed to fetch needed reporting data'));
        }
      } finally {
        setInitialDataFetched(true);
        setIsFetchingInitialData(false);
      }
    };

    fetchData();
  }, [
    workspaceId,
    organisationId,
    isFutrliTemplate,
    isConsolidation,
    addErrorMessage,
    initialDataFetched,
    isFetchingInitialData,
    intl,
    nodeStaticIds,
    financialConfig
  ]);

  // setting current card
  useEffect(() => {
    if (currentCardId) {
      setCurrentCard(pageLayoutData.find(e => e.i === currentCardId));
    } else {
      setCurrentCard(undefined);
    }
  }, [currentCardId, pageLayoutData]);

  useEffect(() => {
    if (initialDataFetched && finDataRequestsFinished && comparisonDataRequestsFinished) {
      setAllRequestsFinished(true);
    }
  }, [initialDataFetched, finDataRequestsFinished, comparisonDataRequestsFinished]);

  const dsToMap = useMemo(
    () =>
      (dataReferenceMapping?.dataset_references
        ? Object.keys(dataReferenceMapping?.dataset_references || {})
            .map(key => dataReferenceMapping?.dataset_references[key])
            .filter(
              d => d?.data.kind !== 'live' && !forecasts.find(f => f.id === d.id) && hasDataset(pageLayoutData, d.id)
            )
        : []) as MappingData[],
    [pageLayoutData, dataReferenceMapping, forecasts]
  );

  const fieldsToMap = useMemo(
    () =>
      Object.keys(dataReferenceMapping?.line_data_references || {})
        .map(key => ({
          ...dataReferenceMapping?.line_data_references[key],
          ref: key
        }))
        .filter(
          e =>
            hasField(pageLayoutData, e.ref) &&
            !allFinFieldsArray.find(f => f.ref === e.ref) &&
            !futrliFormulaData?.find(f => f.ref === e.ref) &&
            !rowFormulaData?.find(f => f.ref === e.ref)
        ) as MappingData[],
    [pageLayoutData, dataReferenceMapping, allFinFieldsArray, rowFormulaData, futrliFormulaData]
  );

  const canAutoSave = !isViewReportPage && (isTemplate || forecastsFetched);
  const hasUnsavedChanges = () =>
    !isEqual(pageSetup, reportData.current.pageSetup) ||
    !arePageLayoutsEqual(pageLayout, reportData.current.pageLayout) ||
    !areLayoutsDataEqual(pageLayoutData, reportData.current.pageLayoutData);

  // The save gate is open from the moment a change is detected until the auto save PATCH has finished.
  // Financial data requests wait on it, because the backend validates the requested datasets against the
  // report's saved referenced_datasources.
  const saveGate = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);
  const openSaveGate = () => {
    if (!saveGate.current) {
      let resolve: () => void = () => undefined;
      const promise = new Promise<void>(res => {
        resolve = res;
      });
      saveGate.current = { promise, resolve };
    }
  };
  const closeSaveGate = () => {
    saveGate.current?.resolve();
    saveGate.current = null;
  };
  const waitForPendingSave = async () => {
    await saveGate.current?.promise;
  };

  // Requests are built before they wait for the save, so by the time they are sent the report may have changed
  // again (e.g. widgets deleted). The latest layout is used to drop queries that are no longer needed.
  const latestPageLayoutData = useRef(pageLayoutData);
  latestPageLayoutData.current = pageLayoutData;

  const latestPageLayout = useRef(pageLayout);
  latestPageLayout.current = pageLayout;

  // Layout data of deleted widgets can linger, so a widget only exists if it is also in the page layout
  // (the same rule the auto save uses when it computes referenced_datasources).
  const getLatestWidgetDatasets = (cardId: string) =>
    latestPageLayout.current.some(pl => pl.i === cardId)
      ? latestPageLayoutData.current.find(ld => ld.i === cardId)?.[CARD_TYPES.WIDGET]?.selectedDatasets
      : undefined;

  // Reports can only request data for datasets that are in their referenced datasources (actuals, or datasets returned
  // by the available datasources endpoint). Dummy (template placeholder) datasets are never referenced.
  const latestValidForecastIds = useRef(validForecastIds);
  latestValidForecastIds.current = validForecastIds;

  // Forecast ids in the referenced_datasources last saved by this session (null until the first save, as the report
  // detail does not carry them). The backend rejects data requests for forecasts that are not in this list.
  const savedReferencedForecastIds = useRef<Set<string> | null>(null);
  const isReferencableDataset = (datasetId: string) =>
    isTemplate ||
    actualForecastIds.includes(datasetId) ||
    (latestValidForecastIds.current.has(datasetId) &&
      (!savedReferencedForecastIds.current || savedReferencedForecastIds.current.has(datasetId)));

  const filterDatasetQueriesToLatestLayout = <T>(queries: Record<string, T>) =>
    Object.fromEntries(
      Object.entries(queries).filter(([key]) => {
        const [cardId, datasetId] = key.split('#|#');
        return isReferencableDataset(datasetId) && !!getLatestWidgetDatasets(cardId)?.includes(datasetId);
      })
    ) as Record<string, T>;

  const filterComparisonQueriesToLatestLayout = <T>(queries: Record<string, T>) =>
    Object.fromEntries(
      Object.entries(queries).filter(([cardId]) => !!getLatestWidgetDatasets(cardId)?.length)
    ) as Record<string, T>;

  // Layout effects of a commit run before any passive effect, so the gate is already open when child components
  // start fetching data in their effects for the same change (the auto save effect below schedules the PATCH).
  useLayoutEffect(() => {
    if (canAutoSave && hasUnsavedChanges()) {
      openSaveGate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSetup, pageLayout, pageLayoutData, canAutoSave]);

  // never leave data requests waiting on a save that will not happen
  useEffect(
    () => () => {
      if (savingTimerId.current) {
        clearTimeout(savingTimerId.current);
      }
      closeSaveGate();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // auto save report
  useEffect(() => {
    if (canAutoSave) {
      if (hasUnsavedChanges()) {
        if (savingTimerId.current) {
          clearTimeout(savingTimerId.current);
        }
        openSaveGate();
        setIsAutoSaving(true);
        const newPLD = pageLayoutData.filter(pld => pageLayout.find(pl => pl.i === pld.i));
        savingTimerId.current = (setTimeout(() => {
          savingTimerId.current = null;
          (async () => {
            try {
              const newData: {
                pageSetup: PageSetup;
                pageLayout: Layout[];
                pageLayoutData: LayoutData[];
                userTimezone?: string;
                data_reference_mapping?: ReportMappingData;
                sourceTemplateId?: string;
              } = { pageSetup, pageLayout, pageLayoutData: newPLD };
              // we need to update the timezone if it is a number, because we are now saving timezone name (ex: "Europe/London")
              if (!userTimezone?.length || !isNaN(parseInt(userTimezone, 10))) {
                newData.userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              } else {
                newData.userTimezone = userTimezone;
              }

              if (isWorkspaceTemplate) {
                await patchReport(report.id, {
                  data: newData,
                  data_references: getDataReferencesFromReport(({ data: newData } as unknown) as LiveReportDetail),
                  orientation: pageSetup.orientation,
                  page_count: getNumberOfPagesInTemplate(pageLayout, getPageHeightFromSetup(pageSetup))
                });
              } else {
                if (dataReferenceMapping && (dsToMap.length || fieldsToMap.length)) {
                  newData.data_reference_mapping = dataReferenceMapping;
                }
                // Recomputed on every save so the backend never has to derive it from the widgets.
                const referencedDatasources = getReferencedDatasources(
                  newData.pageLayoutData,
                  organisationId,
                  isConsolidation,
                  validForecastIds
                );
                await patchReport(report.id, {
                  data: newData,
                  orientation: pageSetup.orientation,
                  page_count: getNumberOfPagesInTemplate(pageLayout, getPageHeightFromSetup(pageSetup)),
                  referenced_datasources: referencedDatasources
                });
                savedReferencedForecastIds.current = new Set(
                  referencedDatasources.map(ds => ds.forecast_id).filter((id): id is string => !!id)
                );
              }
              reportData.current = { pageSetup, pageLayout, pageLayoutData };
            } catch (e) {
              Logger.error(e as string);
            } finally {
              setIsAutoSaving(false);
              // a newer change has scheduled another save, data requests must wait for that one too
              if (!savingTimerId.current) {
                closeSaveGate();
              }
            }
          })();
        }, SAVE_DELAY_TIME) as unknown) as number;
      }
    }
  }, [
    pageSetup,
    pageLayout,
    pageLayoutData,
    report.id,
    isViewReportPage,
    isWorkspaceTemplate,
    isTemplate,
    userTimezone,
    dataReferenceMapping,
    patchReport,
    reportSourceTemplateId,
    dsToMap,
    fieldsToMap,
    getDataReferencesFromReport,
    organisationId,
    isConsolidation,
    forecastsFetched,
    validForecastIds
  ]);

  // update report colors
  const reportColors = useMemo(() => {
    if (!isViewReportPage) {
      const colors: string[] = [...defaultColors];
      pageLayoutData.forEach(e => {
        if (e.type === CARD_TYPES.WIDGET) {
          if (e[CARD_TYPES.WIDGET]?.finFieldsOptions.length) {
            e[CARD_TYPES.WIDGET]?.finFieldsOptions.forEach(f => {
              if (f.options?.colors) {
                if (Array.isArray(f.options?.colors)) {
                  f.options?.colors.forEach(c => {
                    if (c && !colors.includes(c.toUpperCase())) {
                      colors.unshift(c.toUpperCase());
                    }
                  });
                } else {
                  for (const k in f.options?.colors) {
                    if (f.options?.colors.hasOwnProperty(k)) {
                      const c = f.options?.colors?.[k];
                      if (c && !colors.includes(c.toUpperCase())) {
                        colors.unshift(c.toUpperCase());
                      }
                    }
                  }
                }
              }
            });
          }
          const tc: string[] = [];
          ([
            'headerRow',
            'forecastsRow',
            'normalRow',
            'categoriesRow',
            'groupsRow',
            'accountsRow',
            'assumptionsRow',
            'formulasRow'
          ] as RowTypes[]).forEach(type => {
            tc.push(
              e[CARD_TYPES.WIDGET]?.[type]?.color || '',
              e[CARD_TYPES.WIDGET]?.[type]?.fill || '',
              e[CARD_TYPES.WIDGET]?.[type]?.fillFuture || '',
              e[CARD_TYPES.WIDGET]?.[type]?.fillPast || '',
              e[CARD_TYPES.WIDGET]?.[type]?.fillVariance || '',
              e[CARD_TYPES.WIDGET]?.[type]?.fillEven || '',
              e[CARD_TYPES.WIDGET]?.[type]?.fillFutureEven || '',
              e[CARD_TYPES.WIDGET]?.[type]?.fillPastEven || '',
              e[CARD_TYPES.WIDGET]?.[type]?.fillVarianceEven || ''
            );
          });
          tc.forEach(c => {
            if (c && !colors.includes(c.toUpperCase())) {
              colors.unshift(c.toUpperCase());
            }
          });
        }
      });
      return colors;
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLayoutData, isViewReportPage]);

  // init state from report
  useEffect(() => {
    // we only want to update the state once and only after needed data is fetched
    if (!stateInitialized.current && initialDataFetched && financialConfig) {
      setIsReady(false);
      const {
        pageSetup: newReportPageSetup,
        pageLayout: newReportPageLayout,
        pageLayoutData: newReportPageLayoutData
      } = getStateFromReport(report);
      setPageSetup(newReportPageSetup);
      setPageLayout(newReportPageLayout);
      const resolvedPageLayoutData = newReportPageLayoutData;
      const formulas = [...(rowFormulaData || []), ...(futrliFormulaData || [])];

      // Detect deleted refs and add them to mapping data so the mapping modal can handle them
      const deletedMappingEntries = getDeletedFinFieldMappingEntries(
        resolvedPageLayoutData,
        allFinFields,
        formulas,
        dataReferenceMapping
      );
      if (Object.keys(deletedMappingEntries).length) {
        setDataReferenceMapping(prev => ({
          line_data_references: {
            ...prev?.line_data_references,
            ...deletedMappingEntries
          },
          dataset_references: prev?.dataset_references || {}
        }));
      }

      setPageLayoutData(
        newReportPageLayout.length
          ? updateFinFields(resolvedPageLayoutData, allFinFields, formulas, dataReferenceMapping)
          : []
      );
      setIsPendingNewFinData(true);
      setDispatchedReports(undefined);
      setScheduleReports(undefined);
      stateInitialized.current = true;
    }
  }, [
    report,
    allFinFields,
    rowFormulaData,
    futrliFormulaData,
    isFutrliTemplate,
    initialDataFetched,
    financialConfig,
    dataReferenceMapping
  ]);

  // When a report is opened in edit mode, datasets used by widgets that are no longer available (e.g. deleted forecasts)
  // are added to the data reference mapping and the user is asked to map them. If the user closes the modal, the
  // datasets keep being flagged on the report and on the widgets that use them, like datasets from a workspace template.
  const unavailableDatasetsChecked = useRef(false);
  useEffect(() => {
    if (
      unavailableDatasetsChecked.current ||
      isTemplate ||
      isViewReportPage ||
      !forecastsFetched ||
      !stateInitialized.current
    ) {
      return;
    }
    unavailableDatasetsChecked.current = true;

    // datasets of a workspace template that map to the actuals are not missing
    const datasetsMappedToActuals = Object.values(dataReferenceMapping?.dataset_references || {})
      .filter(d => d?.data?.kind === 'live')
      .map(d => d.id);
    const unavailableDatasetIds = getUnavailableDatasetIds(pageLayoutData, validForecastIds).filter(
      id => !datasetsMappedToActuals.includes(id)
    );
    const dummyDatasetIds = getDummyTemplateDSInReport(pageLayoutData);
    if (!unavailableDatasetIds.length && !dummyDatasetIds.length) {
      return;
    }

    if (!unavailableDatasetIds.length) {
      // only datasets coming from a futrli template
      setDatasetsToMap(dummyDatasetIds);
      setMapDatasetsModalOpen(true);
      return;
    }

    const datasetName = intl.formatMessage({ id: 'reporting.missing_dataset', defaultMessage: 'Missing dataset' });
    setDataReferenceMapping(prev => ({
      line_data_references: prev?.line_data_references || {},
      dataset_references: {
        ...Object.fromEntries(
          unavailableDatasetIds.map(id => [
            id,
            { id, name: datasetName, type: 'dataset', data: { kind: UNAVAILABLE_DATASET_KIND } } as MappingData
          ])
        ),
        ...prev?.dataset_references
      }
    }));
    // a single modal maps the unavailable datasets together with the ones coming from a futrli template
    setDatasetsToMap([...dummyDatasetIds, ...unavailableDatasetIds]);
    setDataRefsToMap([]);
    setMapTemplateDataModalOpen(true);
  }, [
    dataReferenceMapping,
    forecastsFetched,
    getDummyTemplateDSInReport,
    intl,
    isTemplate,
    isViewReportPage,
    pageLayoutData,
    validForecastIds
  ]);

  // Cached config alone is not enough: widget requests also need the report metadata.
  useEffect(() => {
    if (initialDataFetched && financialConfig && !isFetchingFinData && !isFetchingComparisonData &&
        isPendingNewFinData && (isTemplate || forecastsFetched) && allFinFields.length) {
      setIsPendingNewFinData(false);
      getReportDataForLiveReport();
      if (!isTemplate && !isDashboardPage) {
        getScheduleDataForReport();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingNewFinData, forecastsFetched, allFinFields, isDashboardPage, isTemplate, isConsolidation,
    initialDataFetched, financialConfig, isFetchingFinData, isFetchingComparisonData]);

  useEffect(() => {
    setIsReady(!(isFinancialConfigLoading || isPendingNewFinData || isFetchingInitialData || isFetchingFinData));
  }, [isFinancialConfigLoading, isPendingNewFinData, isFetchingInitialData, isFetchingFinData]);

  const fetchCashflowMetrics = async () => {
    if (!cashflowMetrics && !isFetchingCFMetrics) {
      setIsFetchingCFMetrics(true);
      const performanceMetricsCashflow = await fetchPerformanceMetricsCashflow(workspaceId, organisationId);
      setCashflowMetrics(performanceMetricsCashflow);
      setIsFetchingCFMetrics(false);
    }
  };

  const fetchProfitAndLossMetrics = async () => {
    if (!profitAndLossMetrics && !isFetchingPandLMetrics) {
      setIsFetchingPandLMetrics(true);
      const fetchedProfitAndLossMetrics = await fetchPerformanceMetricsProfitAndLoss(workspaceId, organisationId);
      setProfitAndLossMetrics(fetchedProfitAndLossMetrics);
      setIsFetchingPandLMetrics(false);
    }
  };

  const fetchBalanceSheetMetrics = async () => {
    if (!balanceSheetMetrics && !isFetchingBSMetrics) {
      setIsFetchingBSMetrics(true);
      const performanceMetricsOverview = await fetchPerformanceMetricsBalanceSheet(workspaceId, organisationId);
      setBalanceSheetMetrics(performanceMetricsOverview);
      setIsFetchingBSMetrics(false);
    }
  };

  const selectedFinFieldsIncChildren = useMemo(() => {
    let fwc: DataField[] = [];
    const widgetOptions = currentCard?.[CARD_TYPES.WIDGET] as WidgetData;
    if (widgetOptions) {
      (widgetOptions.selectedFinFields || []).forEach(f => {
        const ffl = getFieldWithChildren(allFinFields, f.ref, f.isSecondary) || f;
        const fieldOptions = (widgetOptions.finFieldsOptions || []).find(e => e.ref === f.ref);
        fwc = fwc.concat(
          getFieldsByGroupingWithLevel(
            ffl,
            fieldOptions?.options?.grouping || [],
            !!f.isSecondary,
            [],
            fieldOptions?.options?.excludedChildren,
            fieldOptions?.options?.includedChildren
          )
        );
      });
    }
    return fwc;
  }, [currentCard, allFinFields]);

  const updateReportLocale = async (newLocale: string) => {
    setIsAutoSaving(true);
    await patchReport(report.id, { locale: newLocale });
    setIsAutoSaving(false);
  };

  const updateReportTitle = async (name: string) => {
    setIsAutoSaving(true);
    await patchReport(report.id, { name });
    setIsAutoSaving(false);
  };

  const updateTemplateTags = async (tags: string[]) => {
    if (isTemplate) {
      setIsAutoSaving(true);
      await patchReport(report.id, { tags });
      setIsAutoSaving(false);
    }
  };

  const buildFinDataQueries = (): Record<string, V2FinancialDatasetRequest> => {
    const queries: Record<string, FinancialDatasetRequest> = {};
    pageLayoutData.forEach(ld => {
      // Datasets are requested for every widget, including comparison tables. Datasets and
      // comparisons are sent in the same request, so this costs no extra round trip and means
      // switching a comparison table to a chart (which ignores comparisons) can render
      // immediately from data we already hold instead of triggering a refetch.
      const queriesArr = getFinDataQueryForWidget(
        ld,
        allFinFields,
        [...(rowFormulaData || []), ...(futrliFormulaData || [])],
        forecasts,
        true,
        !!isFutrliTemplate,
        financialConfig,
        isConsolidation
      );
      queriesArr.forEach(query => {
        if (query.fields.length && query.period_count && query.period_type && query.start_date) {
          queries[`${ld.i}#|#${query.forecast_id}`] = withActualsForecastId(query, !!isFutrliTemplate);
        }
      });
    });
    return transformFinancialDataQueriesForV2(queries);
  };

  const buildComparisonQueries = (): Record<string, SingleOrgForecastComparisonRequest> => {
    const queries: Record<string, ForecastComparisonRequest> = {};
    pageLayoutData.forEach(ld => {
      const query = getComparisonQueryForWidget(
        ld,
        allFinFields,
        [...(rowFormulaData || []), ...(futrliFormulaData || [])],
        [...forecasts, ...actualForecasts],
        !!isFutrliTemplate,
        financialConfig
      );
      if (query) {
        queries[ld.i] = query;
      }
    });
    return transformComparisonQueriesForV2(queries);
  };

  /**
   * Loads all report data for the current page.
   *
   * Datasets and comparisons are sent as a single financial-data request.
   */
  const getReportDataForLiveReport = async () => {
    if (!financialConfig) {
      return;
    }

    const datasetQueries = buildFinDataQueries();
    const comparisonQueries = buildComparisonQueries();
    const hasDatasets = Object.keys(datasetQueries).length !== 0;
    const hasComparisons = Object.keys(comparisonQueries).length !== 0;

    if (!hasDatasets && !hasComparisons) {
      setFinDataRequestsFinished(true);
      setComparisonDataRequestsFinished(true);
      return;
    }

    if (isFetchingFinData || isFetchingComparisonData) {
      return;
    }

    try {
      setIsFetchingFinData(true);
      setIsFetchingComparisonData(true);

      await waitForPendingSave();
      const currentDatasetQueries = filterDatasetQueriesToLatestLayout(datasetQueries);
      const currentComparisonQueries = filterComparisonQueriesToLatestLayout(comparisonQueries);
      const hasCurrentDatasets = Object.keys(currentDatasetQueries).length !== 0;
      const hasCurrentComparisons = Object.keys(currentComparisonQueries).length !== 0;

      // nothing to request if the widgets that needed data were removed while waiting for the save
      if (!hasCurrentDatasets && !hasCurrentComparisons) {
        return;
      }

      const { datasets, comparisons } = await getReportBulkData(
        workspaceId,
        organisationId,
        {
          ...(hasCurrentDatasets ? { datasets: currentDatasetQueries } : {}),
          ...(hasCurrentComparisons ? { comparisons: currentComparisonQueries } : {})
        },
        isConsolidation,
        financialDataReportId,
        isWorkspaceTemplate
      );

      if (hasCurrentDatasets) {
        setLayoutFinData(convertFinDataToIncludeForecastId(datasets));
      }
      if (hasCurrentComparisons) {
        setLayoutComparisonData(comparisons);
      }
    } catch (error) {
      if (getErrorStatus(error) !== ERROR_STATUS._404) {
        addErrorMessage?.(new Error('Failure to fetch bulk live Financial Data'));
      }
    } finally {
      setIsFetchingFinData(false);
      setIsFetchingComparisonData(false);
      setFinDataRequestsFinished(true);
      setComparisonDataRequestsFinished(true);
    }
  };

  const getFinDataForLiveReport = async () => {
    if (financialConfig) {
      const queries = buildFinDataQueries();

      if (Object.keys(queries).length !== 0) {
        try {
          if (!isFetchingFinData) {
            setIsFetchingFinData(true);

            await waitForPendingSave();
            const currentQueries = filterDatasetQueriesToLatestLayout(queries);
            if (Object.keys(currentQueries).length !== 0) {
              const { datasets } = await getReportBulkData(
                workspaceId,
                organisationId,
                { datasets: currentQueries },
                isConsolidation,
                financialDataReportId,
                isWorkspaceTemplate
              );
              const newData = convertFinDataToIncludeForecastId(datasets);
              setLayoutFinData(newData);
            }
          }
        } catch (error) {
          if (getErrorStatus(error) !== ERROR_STATUS._404) {
            addErrorMessage?.(new Error('Failure to fetch bulk live Financial Data'));
          }
        } finally {
          setIsFetchingFinData(false);
          setFinDataRequestsFinished(true);
        }
      } else {
        setFinDataRequestsFinished(true);
      }
    }
  };

  const getScheduleDataForReport = async () => {
    try {
      if (!isFetchingScheduleData) {
        const [fetchedScheduledReports, fetchedDispatchedReports] = await Promise.all([
          getScheduledReports(workspaceId, organisationId, { report_id: report.id }, isConsolidation),
          getDispatchedReports(
            workspaceId,
            organisationId,
            { report_id: report.id, status: 'awaiting-permission' },
            isConsolidation
          )
        ]);
        setScheduleReports(fetchedScheduledReports);
        setDispatchedReports(fetchedDispatchedReports);
      }
    } catch (e) {
      if (getErrorStatus(e) !== ERROR_STATUS._404) {
        addErrorMessage?.(new Error('Failure to fetch schedule data for report'));
      }
    } finally {
      setIsFetchingScheduleData(false);
      setFinDataRequestsFinished(true);
    }
  };

  const revalidateScheduleReportData = async () => {
    getScheduleDataForReport();
  };

  const getComparisonDataForQuery = async (query: Record<string, ForecastComparisonRequest>) => {
    if (Object.keys(query).length !== 0) {
      try {
        if (!isFetchingComparisonData && financialConfig) {
          setIsFetchingComparisonData(true);

          await waitForPendingSave();
          const currentQuery = filterComparisonQueriesToLatestLayout(query);
          if (Object.keys(currentQuery).length !== 0) {
            const { comparisons } = await getReportBulkData(
              workspaceId,
              organisationId,
              { comparisons: transformComparisonQueriesForV2(currentQuery) },
              isConsolidation,
              financialDataReportId,
              isWorkspaceTemplate
            );
            setLayoutComparisonData({ ...layoutComparisonData, ...comparisons });
          }
        }
      } catch (error) {
        if (getErrorStatus(error) !== ERROR_STATUS._404) {
          addErrorMessage?.(new Error('Failure to fetch multi forecast comparison Data'));
        }
      } finally {
        setIsFetchingComparisonData(false);
      }
    }
  };

  const getRowFormulas = async () => {
    if (financialConfig) {
      try {
        const fetchedRFData = isFutrliTemplate
          ? []
          : await fetchAvailableRowFormulaData(workspaceId, organisationId, isConsolidation);
        const fetchedTFData = isConsolidation
          ? []
          : await fetchAvailableTemplateFormulaData(workspaceId, organisationId, isConsolidation);

        setRowFormulaData([...(fetchedRFData || []), ...(isFutrliTemplate ? fetchedTFData || [] : [])]);
        setFutrliFormulaData(fetchedTFData);
      } catch (error) {
        if (getErrorStatus(error) !== ERROR_STATUS._404) {
          addErrorMessage?.(new Error('Failure to fetch live row formula data'));
        }
      }
    }
  };

  const getPageWidth = () => {
    const size = pageSizesOptions.find(e => e.value === pageSetup.pageSize) || pageSizesOptions[0];
    const {
      dimensions: { width, height }
    } = size;
    return pageSetup.orientation === 'portrait' ? width : height;
  };

  const getPageHeight = () => {
    return getPageHeightFromSetup(pageSetup);
  };

  const emptyFinDataForWidget = (i: string) => {
    if (layoutFinData[i]) {
      const newData = { ...layoutFinData };
      delete newData[i];
      setLayoutFinData(newData);
    }
  };

  const emptyComparisonDataForWidget = (i: string) => {
    if (layoutComparisonData?.[i]) {
      const newData = { ...layoutComparisonData };
      delete newData[i];
      setLayoutComparisonData(newData);
    }
  };

  const updateFinDataForWidget = (id: string, queries: FinancialDatasetRequest[]) => {
    emptyFinDataForWidget(id);
    const query = multiForecastQueryFromQueriesArr(id, queries, !!isFutrliTemplate);
    if (Object.keys(query).length !== 0) {
      waitForPendingSave().then(() => {
        const currentQuery = filterDatasetQueriesToLatestLayout(query);
        if (Object.keys(currentQuery).length !== 0) {
          reloadWidgetData(id, currentQuery);
        }
      });
    }
  };

  const updateComparisonDataForWidget = (i: string, query: ForecastComparisonRequest) => {
    if (gettingComparisonDataTimerId.current) {
      clearTimeout(gettingComparisonDataTimerId.current);
    }
    gettingComparisonDataTimerId.current = (setTimeout(() => {
      emptyComparisonDataForWidget(i);
      getComparisonDataForQuery({ [i]: query });
    }, GET_FIN_DATA_DELAY_TIME) as unknown) as number;
  };

  const setPageSize = (pageSize: PAGE_SIZES) => {
    setPageSetup({ ...pageSetup, pageSize });
  };
  const setPageOrientation = (orientation: 'portrait' | 'landscape' | 'dashboard') => {
    setPageSetup({ ...pageSetup, orientation });
  };
  const setBackgroundColor = (backgroundColor: string) => {
    setPageSetup({ ...pageSetup, backgroundColor });
  };
  const setMargins = (margins: Margins[]) => {
    setPageSetup({ ...pageSetup, margins });
  };
  const setShowMargins = (showMargins: boolean) => {
    setPageSetup({ ...pageSetup, showMargins });
  };
  const setZoom = (zoom: number) => {
    setPageSetup({ ...pageSetup, zoom });
  };

  const updateWidgetData = (data: WidgetData) => {
    if (!currentCardId) {
      Logger.warn('calling updateWidgetData without currentCardId');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[updateWidgetData]: current card ids are not equal, stopping update');
      return;
    }
    const currentData = currentCard || {
      i: currentCardId,
      type: CARD_TYPES.WIDGET
    };
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentData, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const updateTextData = (data: TextData) => {
    if (!currentCardId) {
      Logger.warn('calling updateTextData without currentCardId');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[updateTextData]: current card ids are not equal, stopping update');
      return;
    }
    const currentData = currentCard || {
      i: currentCardId,
      type: CARD_TYPES.TEXT
    };
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentData, [CARD_TYPES.TEXT]: data }
    ]);
  };

  const updateSelectedImage = (img: ImageData) => {
    if (!currentCardId) {
      Logger.warn('calling updateSelectedImage without currentCardId');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[updateSelectedImage]: current card ids are not equal, stopping update');
      return;
    }
    const currentData = currentCard || {
      i: currentCardId,
      type: CARD_TYPES.IMAGE
    };
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentData, [CARD_TYPES.IMAGE]: { ...img } }
    ]);
  };

  const setSelectedFinFields = (
    fields: DataField[],
    excludedChildren?: Record<string, string[]>,
    includedChildren?: Record<string, string[]>
  ) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setSelectedFinField on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setSelectedFinFields]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedFinFields: fields,
      finFieldsOptions: fields
        .map(f => {
          // we call setSelectedFinFields either when adding a batch of new financial fields
          // or when swapping the position of financial fields.
          // in the latter case, we want to preserve financial fields options.
          // we also want to add any excluded or included children
          const fieldOptions = currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions.find(o => o.ref === f.ref);
          const fwc = getFieldWithChildren(allFinFields, f.ref) || f;
          const newOptions: { ref: string; options: FinFieldOptions } = {
            ref: f.ref,
            options: fieldOptions?.options || {
              grouping: getDefaultGrouping(fwc, currentCard[CARD_TYPES.WIDGET]?.widgetType as WIDGET_TYPES),
              rounding: ROUNDING_OPTIONS.INTEGER
            }
          };
          if (excludedChildren?.[f.ref]) {
            newOptions.options.excludedChildren = excludedChildren[f.ref];
          }
          if (includedChildren?.[f.ref]) {
            newOptions.options.includedChildren = includedChildren[f.ref];
          }
          return newOptions;
        })
        .sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0))
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };
  const addSelectedFinField = (field: DataField) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling addSelectedFinField on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[addSelectedFinField]: current card ids are not equal, stopping update');
      return;
    }
    const fwc = getFieldWithChildren(allFinFields, field.ref) || field;
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedFinFields: [...(currentCard[CARD_TYPES.WIDGET]?.selectedFinFields || []), field],
      finFieldsOptions: [
        ...(currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions.filter(e => e.ref !== field.ref) || []),
        {
          ref: field.ref,
          options: {
            grouping: getDefaultGrouping(fwc, currentCard[CARD_TYPES.WIDGET]?.widgetType as WIDGET_TYPES),
            rounding: ROUNDING_OPTIONS.INTEGER
          }
        }
      ].sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0))
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };
  const removeSelectedFinField = (ref: string) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling removeSelectedFinField on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[removeSelectedFinField]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedFinFields: getFinFieldsWithoutChild(currentCard[CARD_TYPES.WIDGET]?.selectedFinFields || [], ref),
      finFieldsOptions: (currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions || []).filter(e => e.ref !== ref)
    } as WidgetData;

    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const addSelectedDataset = (ref: string) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling addSelectedDataset on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[addSelectedDataset]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedDatasets: [...(currentCard[CARD_TYPES.WIDGET]?.selectedDatasets || []), ref]
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };
  const setSelectedDatasets = (datasets: string[]) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setSelectedDatasets on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setSelectedDatasets]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedDatasets: datasets
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  /**
   * Handles the selection of a forecast / dataset in the sidebar
   * @param selectedItem - The forecast or dataset deing dropped or double clicked
   * @param targetType - The target area for the selected item
   * @param isSecondaryAxis - Flags if the selected area is the secondary area (secondary axis)
   */
  const addSelectFinFieldOrDataset = (
    selectedItem: {
      type: DatasetOptionType;
      ref: string;
      name: string;
      classifier?: ClassifierType;
    },
    targetType: DatasetOptionType,
    isSecondaryAxis = false
  ) => {
    // If a widget is not selected, or if this item has already been added to the currently selected widget, return.
    if (!currentCard || currentCard[CARD_TYPES.WIDGET]?.selectedDatasets?.includes(selectedItem.ref)) {
      return;
    }

    const selectedDatasets = getSelectedDatasets();
    const selectedFinFields = getSelectedFinFields();
    const selectedVariances = getSelectedVarianceFields();

    if (selectedItem.type !== targetType && selectedItem.type !== 'formula' && selectedItem.type !== 'column-formula') {
      return;
    }
    if (selectedItem.type === 'dataset' && !selectedItem.classifier && !selectedDatasets.includes(selectedItem.ref)) {
      addSelectedDataset(selectedItem.ref);
    }
    const allowDroppingFinFieldOrRowFormula =
      ['financial', 'formula'].includes(selectedItem.type) && !selectedFinFields.find(e => e.ref === selectedItem.ref);

    const allowDroppingColumnFormula =
      selectedItem.type === 'column-formula' &&
      !selectedVariances.find(e => e.ref === selectedItem.ref) &&
      selectedFinFields.length > 0;

    if (typeof selectedItem.classifier !== 'undefined') {
      // be careful with logic check here, ClassifierType can be an empty string (which is a valid defined value)
      if (allowDroppingFinFieldOrRowFormula || allowDroppingColumnFormula) {
        if (selectedItem.type === 'financial') {
          addSelectedFinField({
            ref: selectedItem.ref,
            name: selectedItem.name,
            classifier: selectedItem.classifier,
            isSecondary: isSecondaryAxis
          });
        } else if (selectedItem.type === 'formula') {
          addSelectedFinField({
            ref: selectedItem.ref,
            name: selectedItem.name,
            children: [],
            classifier: selectedItem.classifier,
            isSecondary: isSecondaryAxis
          });
        } else {
          addSelectedVarianceField({
            ref: selectedItem.ref,
            name: selectedItem.name,
            children: [],
            classifier: selectedItem.classifier
          });
        }
      }
      if (selectedItem.type === 'calendar') {
        setSelectedCalFields(
          [
            {
              ref: selectedItem.ref,
              name: selectedItem.name,
              classifier: selectedItem.classifier
            }
          ],
          {
            type: selectedItem.ref,
            startDate: PERIOD_OPTIONS.THIS_PERIOD,
            comparision: COMPARISON_OPTIONS.PREVIOUS,
            periods: DEFAULT_PERIOD_NUMBER
          }
        );
      }
    }
  };

  const setSelectedComparisons = (ref: string, comparisons: string[][]) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setSelectedComparisons on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setSelectedComparisons]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedComparisons: [
        // Temp: changing one comparison changes the other
        ...(currentCard[CARD_TYPES.WIDGET]?.selectedComparisons || [])
          .filter(r => r.ref !== ref)
          .map(c => ({ ref: c.ref, comparisons })),
        { ref, comparisons }
      ]
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const removeSelectedDataset = (ref: string) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling removeSelectedDataset on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[removeSelectedDataset]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedDatasets: (currentCard[CARD_TYPES.WIDGET]?.selectedDatasets || []).filter(e => e !== ref),
      // if we remove a dataset, we also remove comparisons and dataset comparison variances
      selectedComparisons: [],
      selectedVariances: (currentCard[CARD_TYPES.WIDGET]?.selectedVariances || []).filter(
        v => !datasetComparisonChildren.find(dc => dc.ref === v.ref)
      )
    } as WidgetData;

    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const addSelectedVarianceField = (field: DataField) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling addSelectedVarianceField on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[addSelectedVarianceField]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedVariances: [...(currentCard[CARD_TYPES.WIDGET]?.selectedVariances || []), field]
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const removeSelectedVarianceField = (ref: string) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling removeSelectedVarianceField on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[removeSelectedVarianceField]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedVariances: (currentCard[CARD_TYPES.WIDGET]?.selectedVariances || []).filter(e => e.ref !== ref),
      selectedComparisons: (currentCard[CARD_TYPES.WIDGET]?.selectedComparisons || []).filter(e => e.ref !== ref)
    } as WidgetData;

    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setSelectedVariances = (fields: DataField[]) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setSelectedVariances on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setSelectedVariances]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedVariances: fields
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setSelectedCalFields = (fields: DataField[], calendarOptions?: CalendarOptions | undefined) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setSelectedCalFields on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setSelectedCalFields]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedCalFields: fields,
      calendarOptions: calendarOptions || defaultCalendarOptions
    } as WidgetData;

    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const removeSelectedCalField = (ref: string) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling removeSelectedCalField on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[removeSelectedCalField]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      selectedCalFields: (currentCard[CARD_TYPES.WIDGET]?.selectedCalFields || []).filter(e => e.ref !== ref),
      calendarOptions: undefined
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setCalendarOptions = (calendarOptions: CalendarOptions | undefined) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setCalendarOptions on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setCalendarOptions]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = { ...currentCard[CARD_TYPES.WIDGET], calendarOptions } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setPrimaryAxisOptions = (primaryAxisOptions: AxisOptions | undefined) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setPrimaryAxisOptions on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setPrimaryAxisOptions]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = { ...currentCard[CARD_TYPES.WIDGET], primaryAxisOptions } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };
  const setSecondaryAxisOptions = (secondaryAxisOptions: AxisOptions | undefined) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setSecondaryAxisOptions on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setSecondaryAxisOptions]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = { ...currentCard[CARD_TYPES.WIDGET], secondaryAxisOptions } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setFinFieldOptions = (ref: string, options: FinFieldOptions) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setFinFieldOptions on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setFinFieldOptions]: current card ids are not equal, stopping update');
      return;
    }
    const oldOptions = currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions.find(e => e.ref === ref) || {
      ref,
      options: {}
    };
    const newOptions = { ...oldOptions, options: { ...oldOptions.options, ...options } };
    if (!isEqual(oldOptions, newOptions)) {
      const data = {
        ...currentCard[CARD_TYPES.WIDGET],
        finFieldsOptions: [
          ...(currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions.filter(e => e.ref !== ref) || []),
          newOptions
        ].sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0))
      } as WidgetData;
      setPageLayoutData([
        ...pageLayoutData.filter(e => e.i !== currentCardId),
        { ...currentCard, [CARD_TYPES.WIDGET]: data }
      ]);
    }
  };

  const setFinFieldOptionsAllFields = (
    options: FinFieldOptions,
    isSecondary?: boolean,
    excludedClassifiers: string[] = []
  ) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setFinFieldOptionsAllFields on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setFinFieldOptionsAllFields]: current card ids are not equal, stopping update');
      return;
    }
    let newFinFieldsOptions = currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions || [];
    let isDifferent = false;
    currentCard[CARD_TYPES.WIDGET]?.selectedFinFields.forEach(field => {
      if (!field.isSecondary === !isSecondary && !excludedClassifiers.includes(field.classifier)) {
        const oldOptions = currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions.find(e => e.ref === field.ref) || {
          ref: field.ref,
          options: {}
        };
        const newOptions = { ...oldOptions, options: { ...oldOptions.options, ...options } };
        newFinFieldsOptions = [...newFinFieldsOptions.filter(e => e.ref !== field.ref), newOptions];
        if (!isDifferent && !isEqual(oldOptions, newOptions)) {
          isDifferent = true;
        }
      }
    });
    if (isDifferent) {
      const data = {
        ...currentCard[CARD_TYPES.WIDGET],
        finFieldsOptions: newFinFieldsOptions.sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0))
      } as WidgetData;
      setPageLayoutData([
        ...pageLayoutData.filter(e => e.i !== currentCardId),
        { ...currentCard, [CARD_TYPES.WIDGET]: data }
      ]);
    }
  };

  const setMultipleFinFieldOptions = (fieldsOptions: { ref: string; options: FinFieldOptions }[]) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setMultipleFinFieldOptions on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setMultipleFinFieldOptions]: current card ids are not equal, stopping update');
      return;
    }
    const oldOptions = fieldsOptions.map(
      f =>
        currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions.find(e => e.ref === f.ref) || {
          ref: f.ref,
          options: {}
        }
    );
    const newOptions = fieldsOptions.map((f, i) => ({
      ...oldOptions[i],
      options: { ...oldOptions[i].options, ...f.options }
    }));
    const data = {
      ...currentCard[CARD_TYPES.WIDGET],
      finFieldsOptions: [
        ...(currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions.filter(
          e => !fieldsOptions.map(e => e.ref).includes(e.ref)
        ) || []),
        ...newOptions
      ].sort((a, b) => (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0))
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const removeFinFieldOptions = (ref: string) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling removeFinFieldOptions on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[removeFinFieldOptions]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = {
      ...currentCard[CARD_TYPES.WIDGET],
      finFieldsOptions: [...(currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions.filter(e => e.ref !== ref) || [])]
    } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const removeRowFormula = (ref: string) => {
    setPageLayoutData(
      pageLayoutData.map(d => {
        const widgetData = get(d, CARD_TYPES.WIDGET);
        if (widgetData) {
          return {
            ...d,
            [CARD_TYPES.WIDGET]: {
              ...widgetData,
              finFieldsOptions: widgetData.finFieldsOptions.filter(o => o.ref !== ref),
              selectedFinFields: widgetData.selectedFinFields.filter(o => o.ref !== ref)
            }
          };
        } else {
          return d;
        }
      })
    );
  };

  const updateFormulaNameInReport = (ref: string, name: string) => {
    setPageLayoutData(
      pageLayoutData.map(d => {
        const widgetData = get(d, CARD_TYPES.WIDGET);
        if (widgetData) {
          return {
            ...d,
            [CARD_TYPES.WIDGET]: {
              ...widgetData,
              selectedFinFields: widgetData.selectedFinFields.map(o => (o.ref === ref ? { ...o, name } : o))
            }
          };
        } else {
          return d;
        }
      })
    );
  };

  const setWidgetType = (widgetType: WIDGET_TYPES) => {
    if (!currentCardId) {
      Logger.warn('no card selected');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setWidgetType]: current card ids are not equal, stopping update');
      return;
    }
    const currentData = pageLayoutData.find(e => e.i === currentCardId);
    if (!currentData || !currentData[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setWidgetType on non-existing element');
      return;
    }
    const data: WidgetData = { ...currentData[CARD_TYPES.WIDGET], widgetType } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentData, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setWidgetTitle = (widgetTitle: string) => {
    if (!currentCardId) {
      Logger.warn('no card selected');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setWidgetTitle]: current card ids are not equal, stopping update');
      return;
    }
    const currentData = pageLayoutData.find(e => e.i === currentCardId);
    if (!currentData || !currentData[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setWidgetTitle on non-existing element');
      return;
    }
    const data: WidgetData = { ...currentData[CARD_TYPES.WIDGET], widgetTitle } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentData, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setWidgetSubTitle = (widgetSubTitle: string) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setWidgetSubTitle on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setWidgetSubTitle]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = { ...currentCard[CARD_TYPES.WIDGET], widgetSubTitle } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setRowsStyle = (styles: { type: RowTypes; style: TableStyle }[]) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setRowsStyle on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setRowsStyle]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = { ...currentCard[CARD_TYPES.WIDGET] } as WidgetData;
    styles.forEach(e => {
      data[e.type] = e.style;
    });
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const setWidgetDataByKey = (key: string, value: unknown) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setWidgetDataByKey on non-existing element');
      return;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setWidgetDataByKey]: current card ids are not equal, stopping update');
      return;
    }
    const data: WidgetData = { ...currentCard[CARD_TYPES.WIDGET], [key]: value } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  const getWidgetType = useCallback(() => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET] || currentCard?.i !== currentCardId) {
      return undefined;
    }
    return currentCard[CARD_TYPES.WIDGET]?.widgetType as WIDGET_TYPES;
  }, [currentCard, currentCardId]);

  const getWidgetTitle = useCallback(() => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling getWidgetTitle on non-existing element');
      return undefined;
    }
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[getWidgetTitle]: current card ids are not equal, stopping update');
      return;
    }

    return currentCard[CARD_TYPES.WIDGET]?.widgetTitle;
  }, [currentCard, currentCardId]);

  const getWidgetSubTitle = useCallback(() => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling getWidgetSubTitle on non-existing element');
      return undefined;
    }

    if (currentCard?.i !== currentCardId) {
      Logger.warn('[getWidgetSubTitle]: current card ids are not equal, stopping update');
      return undefined;
    }

    return currentCard[CARD_TYPES.WIDGET]?.widgetSubTitle;
  }, [currentCard, currentCardId]);

  const getSelectedFinFields = useCallback(() => {
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[getSelectedFinFields]: current card ids are not equal, stopping update');
      return [];
    }

    return currentCard
      ? localizeFieldNames(currentCard[CARD_TYPES.WIDGET]?.selectedFinFields || [], nodeStaticIds)
      : [];
  }, [currentCard, nodeStaticIds, currentCardId]);

  const getSelectedCalFields = useCallback(() => {
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[getSelectedCalFields]: current card ids are not equal, stopping update');
      return [];
    }

    return currentCard ? currentCard[CARD_TYPES.WIDGET]?.selectedCalFields || [] : [];
  }, [currentCard, currentCardId]);

  const getSelectedVarianceFields = useCallback(() => {
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[getSelectedVarianceFields]: current card ids are not equal, stopping update');
      return [];
    }

    return currentCard ? currentCard[CARD_TYPES.WIDGET]?.selectedVariances || [] : [];
  }, [currentCard, currentCardId]);

  const getFinFieldsOptions = useCallback(() => {
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[getFinFieldsOptions]: current card ids are not equal, stopping update');
      return [];
    }
    return currentCard ? currentCard[CARD_TYPES.WIDGET]?.finFieldsOptions || [] : [];
  }, [currentCard, currentCardId]);

  const getSelectedDatasets = useCallback(() => {
    if (currentCard?.i !== currentCardId) {
      Logger.warn('[getSelectedDatasets]: current card ids are not equal, stopping update');
      return [];
    }

    return currentCard ? currentCard[CARD_TYPES.WIDGET]?.selectedDatasets || [] : [];
  }, [currentCard, currentCardId]);

  const getSelectedComparisons = useCallback(
    (ref: string) => {
      if (currentCard?.i !== currentCardId) {
        Logger.warn('[getSelectedComparisons]: current card ids are not equal, stopping update');
        return [];
      }

      return currentCard
        ? (currentCard[CARD_TYPES.WIDGET]?.selectedComparisons || []).find(e => e.ref === ref)?.comparisons || []
        : [];
    },
    [currentCard, currentCardId]
  );

  const setReportPageNumbersDisplay = (pageNumbersDisplay: PAGE_NUMBERS_DISPLAY) => {
    setPageSetup({ ...pageSetup, pageNumbersDisplay });
  };

  const getDatasetName = (id: string) => {
    let ds =
      id === 'actual-data' || id === 'prior-year-actuals'
        ? actualForecasts.find(f => f.id === id)
        : forecasts.find(f => f.id === id);
    if (!ds) {
      ds = dummyTemplateForecasts.find(f => f.id === id);
    }
    if (!ds && dataReferenceMapping?.dataset_references?.[id]) {
      return dataReferenceMapping.dataset_references[id].data?.kind === UNAVAILABLE_DATASET_KIND
        ? getMissingDatasetName(
            id,
            dsToMap,
            intl.formatMessage({ id: 'reporting.missing_dataset', defaultMessage: 'Missing dataset' })
          )
        : intl.formatMessage({
            id: 'common.dataset',
            defaultMessage: 'Dataset'
          });
    }
    if (!ds) {
      return intl.formatMessage({
        id: 'reporting.dataset_not_found',
        defaultMessage: 'Dataset not found'
      });
    }
    if (ds.kind === 'live' && ds.id !== 'actual-data' && ds.id !== 'prior-year-actuals') {
      return liveForecastName;
    }
    return ds.name;
  };

  const setAppWidgetSelectedPeriod = useCallback(
    (period: PERIODS) => {
      if (!currentCard || !currentCard[CARD_TYPES.APP_WIDGET]) {
        Logger.warn('calling setAppWidgetSelectedPeriod on non-valid element');
        return;
      }
      if (currentCard?.i !== currentCardId) {
        Logger.warn('[setAppWidgetSelectedPeriod]: current card ids are not equal, stopping update');
        return;
      }
      if (
        period === currentCard[CARD_TYPES.APP_WIDGET]?.period ||
        (period === PERIODS.THIS_MONTH && !currentCard[CARD_TYPES.APP_WIDGET]?.period)
      ) {
        return;
      }
      const data: AppWidgetData = { ...currentCard[CARD_TYPES.APP_WIDGET], period } as AppWidgetData;
      setPageLayoutData([
        ...pageLayoutData.filter(e => e.i !== currentCardId),
        { ...currentCard, [CARD_TYPES.APP_WIDGET]: data }
      ]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCard, currentCardId]
  );

  const setWidgetFiltersAndSort = useCallback(
    (filtersAndSort: TableFiltersWithSort) => {
      if (!currentCard || !currentCard[CARD_TYPES.APP_WIDGET]) {
        Logger.warn('calling setWidgetFiltersAndSort on non-valid element');
        return;
      }
      if (currentCard?.i !== currentCardId) {
        Logger.warn('[setWidgetFiltersAndSort]: current card ids are not equal, stopping update');
        return;
      }
      const data: AppWidgetData = {
        ...currentCard[CARD_TYPES.APP_WIDGET],
        widgetFilters: { ...filtersAndSort }
      } as AppWidgetData;
      setPageLayoutData([
        ...pageLayoutData.filter(e => e.i !== currentCardId),
        { ...currentCard, [CARD_TYPES.APP_WIDGET]: data }
      ]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCard, currentCardId]
  );

  const setCollapsedFields = (fields: string[]) => {
    if (!currentCard || !currentCard[CARD_TYPES.WIDGET]) {
      Logger.warn('calling setCollapsedFields on non-existing element');
      return;
    }

    if (currentCard?.i !== currentCardId) {
      Logger.warn('[setCollapsedFields]: current card ids are not equal, stopping update');
      return;
    }

    const data: WidgetData = { ...currentCard[CARD_TYPES.WIDGET], collapsedFields: fields } as WidgetData;
    setPageLayoutData([
      ...pageLayoutData.filter(e => e.i !== currentCardId),
      { ...currentCard, [CARD_TYPES.WIDGET]: data }
    ]);
  };

  return {
    // api data
    profitAndLossMetrics,
    cashflowMetrics,
    balanceSheetMetrics,
    availableFinFields,
    allFinFields,
    allFinFieldsArray,
    cashFlowAndBalanceSheetRefs,
    rowFormulaData,
    futrliFormulaData,
    financialConfig,
    columnCalculations,
    forecasts,
    dispatchedReports,
    scheduledReports,

    // report data
    reportTitle: report.name,
    updateReportTitle,
    updateTemplateTags,
    reportId: report.id,
    reportData: reportData.current,

    // page setup
    pageSetup,
    setPageSetup,
    setPageSize,
    setPageOrientation,
    setBackgroundColor,
    setMargins,
    setShowMargins,
    setZoom,

    // layout and data
    pageLayout,
    setPageLayout,
    pageLayoutData,
    setPageLayoutData,

    // widget data
    currentCardId,
    currentCard,
    setCurrentCardId,
    updateWidgetData,
    setCalendarOptions,
    setPrimaryAxisOptions,
    setSecondaryAxisOptions,
    setFinFieldOptions,
    setFinFieldOptionsAllFields,
    getFinFieldsOptions,
    setMultipleFinFieldOptions,
    removeFinFieldOptions,
    removeRowFormula,
    updateFormulaNameInReport,
    setWidgetType,
    setWidgetTitle,
    setWidgetSubTitle,
    setRowsStyle,
    setWidgetDataByKey,
    setCollapsedFields,

    // text and image panel data
    updateTextData,
    setReportPageNumbersDisplay,
    updateSelectedImage,
    uploadedImages,
    setUploadedImages,

    // widget data fields
    getSelectedDatasets,
    addSelectedDataset,
    setSelectedDatasets,
    removeSelectedDataset,
    getSelectedFinFields,
    addSelectedFinField,
    addSelectFinFieldOrDataset,
    removeSelectedFinField,
    setSelectedFinFields,
    getSelectedCalFields,
    setSelectedCalFields,
    removeSelectedCalField,
    getRowFormulas,
    addSelectedVarianceField,
    removeSelectedVarianceField,
    getSelectedVarianceFields,
    setSelectedVariances,
    getSelectedComparisons,
    setSelectedComparisons,
    setAppWidgetSelectedPeriod,
    setWidgetFiltersAndSort,

    // widget Fin Data
    updateFinDataForWidget,
    emptyFinDataForWidget,
    layoutFinData,
    setLayoutFinData,
    getFinDataForLiveReport,

    // widget Comparison Data
    updateComparisonDataForWidget,
    emptyComparisonDataForWidget,
    layoutComparisonData,
    setLayoutComparisonData,

    // widget data getter
    getWidgetType,
    getWidgetTitle,
    getWidgetSubTitle,
    getPageWidth,
    getPageHeight,

    // Requests info
    allRequestsFinished: allRequestsFinished && !!financialConfig,

    // fetch metrics data
    fetchCashflowMetrics,
    fetchProfitAndLossMetrics,
    fetchBalanceSheetMetrics,

    // other
    workAreaWidth,
    setWorkAreaWidth,
    isAutoSaving,
    setIsAutoSaving,
    isEditingTitle,
    setIsEditingTitle,
    reportColors,
    isReady,
    selectedFinFieldsIncChildren,
    headerFooterLimits,
    heightAdjusted,
    pageBreaks,
    setPageBreaks,
    getDatasetName,
    isPendingNewFinData,
    setIsPendingNewFinData,
    isTemplate: !!isTemplate,
    isWorkspaceTemplate,
    isPublished: report.status === 'published' || report.status === 'live', // 'published' for workspace templates, and 'live' for reports
    dataReferenceMapping,
    setDataReferenceMapping,
    mapDatasetsModalOpen,
    setMapDatasetsModalOpen,
    mapTemplateDataModalOpen,
    setMapTemplateDataModalOpen,
    datasetsToMap,
    setDatasetsToMap,
    dataRefsToMap,
    setDataRefsToMap,
    reportSourceTemplateId,
    userTimezone,
    revalidateScheduleReportData,
    tags: isTemplate ? (report as ReportTemplateDetail).tags : [],
    reportDsToMap: dsToMap,
    reportFieldsToMap: fieldsToMap,
    isMasterPage: !!isMasterPage,
    itemToBringIntoView,
    setItemToBringIntoView,
    scrollReportTo,
    setScrollReportTo,
    widgetsAwaitingData,
    locale,
    setLocale,
    updateReportLocale
  };
}
